/* eslint-disable @nx/enforce-module-boundaries */
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Route, useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@sweetspot/sweetspot-js/common/hooks/useDebounce'

import { Button } from '@sweetspot/scramble-ds/atoms'
import {
  DataTable,
  DataTableFilters,
  Input,
  InputBase,
  InputContainer,
  InputLeadingContainer,
  Section,
  SectionContent,
  SectionHeader,
  SectionTitle,
  TableSortItem,
} from '@sweetspot/scramble-ds'
import { cn } from '@sweetspot/scramble-ds/utils'

import { RootState } from '@sweetspot/club-portal-legacy/store/types'
import { getVouchers } from '@sweetspot/sweetspot-js/features/vouchers/services/api-platform'
import VoucherModal from '@sweetspot/club-portal-legacy/modals/VoucherModal'
import { FILTER_QUERIES, filters } from './filters'
import { columns } from './columns'
import { FilterType, VoucherTableItem } from './types'
import { formatVouchers } from './utils'

import {
  getFirstMatchingRole,
  hasAccess,
} from '@sweetspot/sweetspot-js/features/userAccess/utils/utils'
import { ACCESS_KEYS } from '@sweetspot/sweetspot-js/features/userAccess/constants/accessTable'

export const VouchersTable = () => {
  const { t, i18n } = useTranslation()
  const history = useHistory()
  const location = useLocation()
  const { path } = useRouteMatch()

  const [activeFilters, setActiveFilters] = useState<FilterType[]>([])
  const [sortItems, setSortItems] = useState<TableSortItem[]>([])
  const [currentPage, setCurrentPage] = useState(0)
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [vouchers, setVouchers] = useState<VoucherTableItem[]>([])
  const [totalItems, setTotalItems] = useState(0)

  const debouncedSearchString = useDebounce(searchString, 500)

  const clubId = useSelector((state: RootState) => state?.golfClub?.selectedId)
  const shouldReload = (location.state as { shouldReload?: boolean })?.shouldReload || false
  const role = useSelector((state: RootState) => getFirstMatchingRole(state.auth.roles))

  const getFilterQuery = useCallback((filters: FilterType[]) => {
    if (!filters || filters.length === 0) {
      return ''
    }
    return filters.map((filter) => FILTER_QUERIES[filter] || '').join('')
  }, [])

  const fetchVouchers = useCallback(
    async (searchQuery = '', currentPage = 0, pageSize = 20) => {
      let query = `?page=${currentPage + 1}&limit=${pageSize}&club=${clubId}`

      const fieldNameMap = {
        name: 'name',
        validFrom: 'starts_at',
        validTo: 'ends_at',
        voucherType: 'code_type',
      }

      if (sortItems.length > 0) {
        sortItems.forEach(({ name, type }) => {
          const fieldName = fieldNameMap[name as keyof typeof fieldNameMap] || name
          if (fieldName && type) {
            query += `&order[${fieldName}]=${type}`
          }
        })
      }

      if (searchQuery) {
        query += `&name=${searchQuery}`
      }

      query += getFilterQuery(activeFilters)

      setIsLoading(true)
      try {
        const response = await getVouchers(query)
        const vouchersList = response['hydra:member']
        const totalItems = response['hydra:totalItems']
        setVouchers(formatVouchers(vouchersList, t, i18n.language, role?.value))
        setTotalItems(totalItems)
      } catch (error) {
        setVouchers([])
        setTotalItems(0)
      } finally {
        setIsLoading(false)
      }
    },
    [clubId, sortItems, getFilterQuery, activeFilters, t, i18n.language, role]
  )

  useEffect(() => {
    fetchVouchers(debouncedSearchString, currentPage, pageSize)
  }, [
    currentPage,
    debouncedSearchString,
    activeFilters,
    pageSize,
    fetchVouchers,
    sortItems,
    shouldReload,
  ])

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchString(event.target.value)
    setCurrentPage(0)
    setIsLoading(true)
  }

  const onFilterChange = (filter: FilterType) => {
    setCurrentPage(0)
    if (!filter) {
      setActiveFilters([])
      setSearchString('')
      fetchVouchers('', 0, pageSize)
      return
    }

    setActiveFilters((prevFilters) => {
      const updatedFilters = [...prevFilters]
      if (!updatedFilters.includes(filter)) {
        updatedFilters.push(filter)
      } else {
        updatedFilters.splice(updatedFilters.indexOf(filter), 1)
      }
      return updatedFilters
    })
  }

  const handleCreate = () => {
    history.push('/vouchers/new')
  }

  const handleRowClick = (voucherId: string | number) => {
    history.push(`/vouchers/${voucherId}`)
  }

  return (
    <Section className="overflow-x-hidden">
      <SectionHeader>
        <SectionTitle>{t('vouchers.title')}</SectionTitle>
      </SectionHeader>
      <SectionContent>
        <DataTable
          columns={columns(t, sortItems, setSortItems)}
          data={vouchers}
          onRowClick={handleRowClick}
          enableRowSelection={false}
          sortItems={sortItems}
          isLoading={isLoading}
          inputSearch={() => (
            <div className="flex w-2/5 flex-row">
              <InputBase className="flex w-full flex-row gap-2 px-4 py-6">
                <InputContainer>
                  <InputLeadingContainer>
                    <i className="fa-regular fa-magnifying-glass" />
                  </InputLeadingContainer>
                  <Input
                    rounding="pill"
                    className="h-10 px-10"
                    placeholder={t('vouchers.placeholder_Search')}
                    value={searchString}
                    onChange={handleSearchChange}
                  />
                </InputContainer>
              </InputBase>
              <div className="flex w-[20px] items-center justify-center ">
                <i
                  className={cn(
                    'fa-regular fa-loader animate-[spin_3s_linear_infinite] text-xl opacity-0',
                    {
                      'opacity-100': isLoading,
                    }
                  )}
                />
              </div>
            </div>
          )}
          extraControls={(table) => (
            <>
              {hasAccess(ACCESS_KEYS.PAGES.VOUCHERS.CREATE, role?.value) && (
                <Button
                  size="small"
                  variant="clear-dark"
                  rounding="pill"
                  onClick={handleCreate}
                  className="hover:!text-text-dark hover:bg-transparent hover:shadow-none focus:!outline-none"
                >
                  <i className="fa-regular fa-square-plus text-lg" />
                </Button>
              )}
              <DataTableFilters
                table={table}
                filters={filters}
                onFilterChange={onFilterChange}
                activeFilters={activeFilters}
              />
            </>
          )}
          pagination={{
            currentPage,
            pageSize,
            totalRows: totalItems,
            onPageChange: (pageIndex: number) => setCurrentPage(pageIndex),
            onPageSizeChange: (size: number) => {
              setCurrentPage(0)
              setPageSize(size)
            },
          }}
        />
      </SectionContent>
      <Route path={`${path}/:voucherID`} component={VoucherModal} />
    </Section>
  )
}
